/**
 * generateThumbnail.js — 256×256 center-cropped JPEG @ Q0.7.
 *
 * FT-342 (2026-07-08) — capture pipeline.
 * See: Maritime-Agents/seaready-app/FT-340-photos/DESIGN-BRIEF.md §4.2 (stage 5).
 *
 * Center-crop → square → 256×256. Target: 15–25 KB per thumbnail.
 *
 * Runs from the same ImageBitmap as `resizeAndEncode` so we only decode once;
 * the orchestrator closes the bitmap once both stages are done.
 */

const THUMB_SIZE = 256;
const THUMB_QUALITY = 0.7;

/**
 * Generate a 256×256 JPEG thumbnail from an ImageBitmap.
 * Center-cropped to a square first, then scaled down.
 * @param {ImageBitmap} bitmap
 * @returns {Promise<{ blob: Blob, mime: 'image/jpeg', bytes: number }>}
 */
export async function generateThumbnail(bitmap) {
  if (!bitmap || typeof bitmap.width !== 'number' || typeof bitmap.height !== 'number') {
    throw new Error('[generateThumbnail] expected ImageBitmap input');
  }
  const { sx, sy, size } = centerCropSquare(bitmap.width, bitmap.height);
  const blob = await paintAndEncode(bitmap, sx, sy, size);
  return { blob, mime: 'image/jpeg', bytes: blob.size };
}

/**
 * Compute a center-crop square region for a source of dimensions w×h.
 * Returns the source-x, source-y, and side length to pass to drawImage.
 */
export function centerCropSquare(w, h) {
  const size = Math.min(w, h);
  const sx = Math.floor((w - size) / 2);
  const sy = Math.floor((h - size) / 2);
  return { sx, sy, size };
}

async function paintAndEncode(bitmap, sx, sy, size) {
  if (typeof OffscreenCanvas === 'function') {
    const off = new OffscreenCanvas(THUMB_SIZE, THUMB_SIZE);
    const ctx = off.getContext('2d');
    if (!ctx) throw new Error('[generateThumbnail] OffscreenCanvas 2d context unavailable');
    // drawImage(src, sx, sy, sw, sh, dx, dy, dw, dh) — 9-arg form: crop + scale
    ctx.drawImage(bitmap, sx, sy, size, size, 0, 0, THUMB_SIZE, THUMB_SIZE);
    if (typeof off.convertToBlob === 'function') {
      return off.convertToBlob({ type: 'image/jpeg', quality: THUMB_QUALITY });
    }
  }
  if (typeof document === 'undefined') {
    throw new Error('[generateThumbnail] no canvas backend available');
  }
  const canvas = document.createElement('canvas');
  canvas.width = THUMB_SIZE;
  canvas.height = THUMB_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('[generateThumbnail] 2d context unavailable');
  ctx.drawImage(bitmap, sx, sy, size, size, 0, 0, THUMB_SIZE, THUMB_SIZE);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('[generateThumbnail] canvas.toBlob returned null'));
      },
      'image/jpeg',
      THUMB_QUALITY,
    );
  });
}

export default generateThumbnail;
